// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Dashboard Hooks
 *
 * Custom React hooks for fetching and managing dashboard data.
 * Uses useState/useEffect patterns consistent with the existing codebase.
 * Includes auto-refresh functionality with configurable intervals.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi } from '../api/dashboardApi';
import { activityLogApi } from '../api/activityLogApi';
import { loaApi } from '../api/loaApi';
import { profileRequestApi } from '../api/profileRequestApi';
import type {
  AdvisorDashboardResponse,
  DashboardSummaryParams,
  DashboardAlertsResponse,
  DashboardAlertsParams,
  DashboardActivityResponse,
  DashboardActivityParams,
  AdvisorPerformanceResponse,
  AdvisorPerformanceParams,
  ResendInviteResponse,
  SendReminderResponse,
} from '../types/dashboard';
import type { DashboardInsightsResponse, DashboardInsightsParams } from '../api/dashboardApi';

// ============================================
// Configuration
// ============================================

const DEFAULT_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const DEFAULT_REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes

/** Adds random jitter (0-30s) to prevent all hooks from refetching simultaneously */
function withJitter(interval: number): number {
  return interval + Math.floor(Math.random() * 30_000);
}

// ============================================
// Dashboard Summary Hook
// ============================================

interface UseDashboardSummaryOptions extends DashboardSummaryParams {
  /** Enable auto-refetch interval (default: 5 minutes) */
  refetchInterval?: number | false;
  /** Time before data is considered stale (default: 2 minutes) */
  staleTime?: number;
  /** Skip initial fetch */
  skip?: boolean;
}

interface UseDashboardSummaryReturn {
  data: AdvisorDashboardResponse | null;
  loading: boolean;
  error: string | null;
  lastFetched: Date | null;
  refetch: () => Promise<void>;
  isStale: boolean;
}

export function useDashboardSummary(
  options: UseDashboardSummaryOptions = {}
): UseDashboardSummaryReturn {
  const {
    all = false,
    advisorId,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    staleTime = DEFAULT_STALE_TIME,
    skip = false,
  } = options;

  const [data, setData] = useState<AdvisorDashboardResponse | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isBackgroundRefetch = false) => {
    try {
      // Stale-while-revalidate: only show loading on initial fetch, not background refetches
      if (!isBackgroundRefetch) {
        setLoading(true);
      }
      setError(null);
      const response = await dashboardApi.getSummary({ all, advisorId });
      setData(response);
      setLastFetched(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      if (!isBackgroundRefetch) {
        setLoading(false);
      }
    }
  }, [all, advisorId]);

  // Initial fetch
  useEffect(() => {
    if (!skip) {
      fetchData(false);
    }
  }, [fetchData, skip]);

  // Auto-refetch interval with jitter to prevent simultaneous refetches
  useEffect(() => {
    if (refetchInterval && !skip) {
      intervalRef.current = setInterval(() => fetchData(true), withJitter(refetchInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refetchInterval, skip]);

  const isStale = lastFetched
    ? Date.now() - lastFetched.getTime() > staleTime
    : true;

  return {
    data,
    loading,
    error,
    lastFetched,
    refetch: () => fetchData(false),
    isStale,
  };
}

// ============================================
// Dashboard Alerts Hook
// ============================================

interface UseDashboardAlertsOptions extends DashboardAlertsParams {
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseDashboardAlertsReturn {
  data: DashboardAlertsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardAlerts(
  options: UseDashboardAlertsOptions = {}
): UseDashboardAlertsReturn {
  const {
    all = false,
    advisorId,
    profileId,
    limit = 10,
    severity,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const [data, setData] = useState<DashboardAlertsResponse | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isBackgroundRefetch = false) => {
    try {
      if (!isBackgroundRefetch) {
        setLoading(true);
      }
      setError(null);
      const response = await dashboardApi.getAlerts({ all, advisorId, profileId, limit, severity });
      setData(response);
    } catch (err) {
      console.error('Failed to fetch dashboard alerts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      if (!isBackgroundRefetch) {
        setLoading(false);
      }
    }
  }, [all, advisorId, profileId, limit, severity]);

  useEffect(() => {
    if (!skip) {
      fetchData(false);
    }
  }, [fetchData, skip]);

  useEffect(() => {
    if (refetchInterval && !skip) {
      intervalRef.current = setInterval(() => fetchData(true), withJitter(refetchInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refetchInterval, skip]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(false),
  };
}

// ============================================
// Dashboard Activity Hook
// ============================================

interface UseDashboardActivityOptions extends DashboardActivityParams {
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseDashboardActivityReturn {
  data: DashboardActivityResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardActivity(
  options: UseDashboardActivityOptions = {}
): UseDashboardActivityReturn {
  const {
    all = false,
    advisorId,
    limit = 10,
    days = 7,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const [data, setData] = useState<DashboardActivityResponse | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isBackgroundRefetch = false) => {
    try {
      if (!isBackgroundRefetch) {
        setLoading(true);
      }
      setError(null);
      const response = await dashboardApi.getActivity({ all, advisorId, limit, days });
      setData(response);
    } catch (err) {
      console.error('Failed to fetch dashboard activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      if (!isBackgroundRefetch) {
        setLoading(false);
      }
    }
  }, [all, advisorId, limit, days]);

  useEffect(() => {
    if (!skip) {
      fetchData(false);
    }
  }, [fetchData, skip]);

  useEffect(() => {
    if (refetchInterval && !skip) {
      intervalRef.current = setInterval(() => fetchData(true), withJitter(refetchInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refetchInterval, skip]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(false),
  };
}

// ============================================
// Advisor Performance Hook (Admin Only)
// ============================================

interface UseAdvisorPerformanceOptions extends AdvisorPerformanceParams {
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseAdvisorPerformanceReturn {
  data: AdvisorPerformanceResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAdvisorPerformance(
  options: UseAdvisorPerformanceOptions = {}
): UseAdvisorPerformanceReturn {
  const {
    limit = 10,
    sortBy = 'profileCount',
    sortDir = 'desc',
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const [data, setData] = useState<AdvisorPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isBackgroundRefetch = false) => {
    try {
      if (!isBackgroundRefetch) {
        setLoading(true);
      }
      setError(null);
      const response = await dashboardApi.getAdvisorPerformance({ limit, sortBy, sortDir });
      setData(response);
    } catch (err) {
      console.error('Failed to fetch advisor performance:', err);
      setError(err instanceof Error ? err.message : 'Failed to load advisor performance');
    } finally {
      if (!isBackgroundRefetch) {
        setLoading(false);
      }
    }
  }, [limit, sortBy, sortDir]);

  useEffect(() => {
    if (!skip) {
      fetchData(false);
    }
  }, [fetchData, skip]);

  useEffect(() => {
    if (refetchInterval && !skip) {
      intervalRef.current = setInterval(() => fetchData(true), withJitter(refetchInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refetchInterval, skip]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(false),
  };
}

// ============================================
// Resend Invite Mutation Hook
// ============================================

interface UseResendInviteReturn {
  resend: (clientId: string, customMessage?: string) => Promise<ResendInviteResponse>;
  loading: boolean;
  error: string | null;
  lastResult: ResendInviteResponse | null;
}

export function useResendInvite(): UseResendInviteReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ResendInviteResponse | null>(null);

  const resend = useCallback(async (clientId: string, customMessage?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardApi.resendInvite(clientId, customMessage);
      setLastResult(response);
      return response;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend invitation';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    resend,
    loading,
    error,
    lastResult,
  };
}

// ============================================
// Send Reminder Mutation Hook
// ============================================

interface UseSendReminderReturn {
  sendReminder: (loaId: string) => Promise<SendReminderResponse>;
  loading: boolean;
  error: string | null;
  lastResult: SendReminderResponse | null;
}

export function useSendReminder(): UseSendReminderReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendReminderResponse | null>(null);

  const sendReminder = useCallback(
    async (loaId: string) => {
      // Phase B (PR 2b): nudge through the unified /api/requests/{id}/nudge.
      // The dashboard alert payload still carries `resourceId = loaId`; we
      // resolve it to the linked Profile Request via loaApi.getLOA. Once the
      // alert backend emits requestId directly, the lookup hop goes away.
      // Per-signatory targeting and custom message are not supported by the
      // unified nudge endpoint — callers used to pass them as no-ops anyway.
      try {
        setLoading(true);
        setError(null);
        const loa = await loaApi.getLOA(loaId);
        if (!loa.requestId) {
          throw new Error(
            'LOA has no linked Profile Request — Profile Requests must be enabled for this tenant.',
          );
        }
        await profileRequestApi.nudge(loa.requestId);
        const response: SendReminderResponse = {
          success: true,
          remindersSent: 1,
          sentTo: [],
          sentAt: new Date().toISOString(),
        };
        setLastResult(response);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send reminder';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    sendReminder,
    loading,
    error,
    lastResult,
  };
}

// ============================================
// Dashboard Insights Hook
// ============================================

interface UseDashboardInsightsOptions extends DashboardInsightsParams {
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseDashboardInsightsReturn {
  data: DashboardInsightsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardInsights(
  options: UseDashboardInsightsOptions = {}
): UseDashboardInsightsReturn {
  const {
    all = false,
    advisorId,
    profileId,
    limit = 5,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const [data, setData] = useState<DashboardInsightsResponse | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isBackgroundRefetch = false) => {
    try {
      if (!isBackgroundRefetch) {
        setLoading(true);
      }
      setError(null);
      const response = await dashboardApi.getInsights({ all, advisorId, profileId, limit });
      setData(response);
    } catch (err) {
      console.error('Failed to fetch dashboard insights:', err);
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      if (!isBackgroundRefetch) {
        setLoading(false);
      }
    }
  }, [all, advisorId, profileId, limit]);

  useEffect(() => {
    if (!skip) {
      fetchData(false);
    }
  }, [fetchData, skip]);

  useEffect(() => {
    if (refetchInterval && !skip) {
      intervalRef.current = setInterval(() => fetchData(true), withJitter(refetchInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refetchInterval, skip]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(false),
  };
}

// ============================================
// Dismiss Activity Items Mutation Hook
// ============================================

export interface DismissActivityItemsInput {
  alertIds: string[];
}

export interface DismissActivityItemsResult {
  alertsDismissed: number;
}

interface UseDismissActivityItemsReturn {
  dismiss: (input: DismissActivityItemsInput) => Promise<DismissActivityItemsResult>;
  loading: boolean;
  error: string | null;
}

/**
 * Bulk-dismisses alerts for the current advisor. Insights are NOT
 * dismissable today (no insight generator yet — nothing for an advisor to
 * dismiss). When insight generation ships, extend this hook to also dismiss
 * insights.
 */
export function useDismissActivityItems(): UseDismissActivityItemsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dismiss = useCallback(async ({ alertIds }: DismissActivityItemsInput) => {
    try {
      setLoading(true);
      setError(null);
      const alertsResult = await activityLogApi.dismissAlerts(alertIds);
      return {
        alertsDismissed: alertsResult.dismissedCount,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to dismiss activity items';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { dismiss, loading, error };
}

// ============================================
// Combined Dashboard Hook
// ============================================

interface UseDashboardOptions {
  all?: boolean;
  advisorId?: string;
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseDashboardReturn {
  summary: AdvisorDashboardResponse | null;
  alerts: DashboardAlertsResponse | null;
  activity: DashboardActivityResponse | null;
  loading: boolean;
  error: string | null;
  lastRefreshed: Date | null;
  isStale: boolean;
  refreshAll: () => Promise<void>;
}

/**
 * Combined hook for fetching all dashboard data.
 * Provides a unified interface for the main dashboard page.
 */
export function useDashboard(options: UseDashboardOptions = {}): UseDashboardReturn {
  const {
    all = false,
    advisorId,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const summaryResult = useDashboardSummary({ all, advisorId, refetchInterval, skip });
  const alertsResult = useDashboardAlerts({ all, advisorId, refetchInterval, skip });
  const activityResult = useDashboardActivity({ all, advisorId, refetchInterval, skip });

  const loading = summaryResult.loading || alertsResult.loading || activityResult.loading;
  const error = summaryResult.error || alertsResult.error || activityResult.error;

  const refreshAll = useCallback(async () => {
    await Promise.all([
      summaryResult.refetch(),
      alertsResult.refetch(),
      activityResult.refetch(),
    ]);
  }, [summaryResult, alertsResult, activityResult]);

  return {
    summary: summaryResult.data,
    alerts: alertsResult.data,
    activity: activityResult.data,
    loading,
    error,
    lastRefreshed: summaryResult.lastFetched,
    isStale: summaryResult.isStale,
    refreshAll,
  };
}

export default useDashboard;
